import React, { useEffect, useState, useMemo } from "react";
import api from "../api/axiosInstance";
import "./GalleryPage.scss";

export default function GalleryPage() {
    const [isDataLoad, setIsDataLoad] = useState(false);
    const [mediaList, setMediaList] = useState([]);

    // загрузка
    const [showLoadCard, setShowLoadCard] = useState(false);
    // структура айтема очереди: {file, title, category, progress, status, error}
    const [items, setItems] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    // usage modal
    const [usageOpen, setUsageOpen] = useState(false);
    const [usageFile, setUsageFile] = useState(null); // { id, title }
    const [usageLoading, setUsageLoading] = useState(false);
    const [usageError, setUsageError] = useState("");
    const [usageList, setUsageList] = useState([]);

    const CATEGORY_OPTIONS = [
        { value: "main",  label: "Основной" },
        { value: "promo", label: "Акции Чио Чио" },
        { value: "ad",    label: "Рекламный ролик" },
    ];

    function categoryLabel(v) {
        const found = CATEGORY_OPTIONS.find(o => o.value === v);
        return found ? found.label : "—";
    }

    function updateContent() {
        // тут важно: с бэка мы должны получать category
        // либо с /admin/files, либо с /getFiles, но в обоих случаях — в ответе category
        api.get("/admin/files")
            .then((resp) => {
                setMediaList(resp.data || []);
                setIsDataLoad(true);
            })
            .catch((e) => {
                setIsDataLoad(true);
                const msg = e?.response?.data?.error || "Ошибка загрузки галереи";
                alert(msg);
            });
    }

    useEffect(() => { updateContent(); }, []);

    // -------- helpers --------
    const toTitle = (name) =>
        (name || "").replace(/\.[^/.]+$/,"").trim() || "Без названия";

    const filesToItems = (fileList) =>
        Array.from(fileList || []).map(f => ({
            file: f,
            title: toTitle(f.name),
            category: "main", // дефолтно "main"
            progress: 0,
            status: "idle", // idle | uploading | done | error
            error: ""
        }));

    // убираем дубликаты по имени+размеру
    function dedup(existing, added) {
        const key = (it) => `${it.file.name}__${it.file.size}`;
        const set = new Set(existing.map(key));
        return added.filter(it => !set.has(key(it)));
    }

    function onFilePick(e) {
        const added = filesToItems(e.target.files);
        setItems(prev => [...prev, ...dedup(prev, added)]);
        e.target.value = "";
    }

    function onDragOver(e) {
        e.preventDefault();
        setDragOver(true);
    }
    function onDragLeave() {
        setDragOver(false);
    }
    function onDrop(e) {
        e.preventDefault();
        setDragOver(false);
        if (!e.dataTransfer?.files?.length) return;
        const added = filesToItems(e.dataTransfer.files);
        setItems(prev => [...prev, ...dedup(prev, added)]);
    }

    function updateItem(idx, patch) {
        setItems(prev =>
            prev.map((it, i) => (i === idx ? { ...it, ...patch } : it))
        );
    }

    function removeItem(idx) {
        setItems(prev => prev.filter((_, i) => i !== idx));
    }

    // -------- upload --------
    async function uploadAll(e) {
        e.preventDefault();
        if (!items.length) return;
        setUploading(true);

        for (let i = 0; i < items.length; i++) {
            const it = items[i];
            if (it.status === "done") continue;

            updateItem(i, { status: "uploading", progress: 0, error: "" });

            const data = new FormData();
            data.append("file", it.file);
            data.append("title", it.title);
            data.append("category", it.category); // <== отправляем категорию

            try {
                await api.post("/admin/files", data, {
                    withCredentials: true,
                    onUploadProgress: (pe) => {
                        if (!pe.total) return;
                        const pct = Math.round((pe.loaded * 100) / pe.total);
                        updateItem(i, { progress: pct });
                    },
                });

                updateItem(i, { status: "done", progress: 100 });
            } catch (err) {
                const msg = err?.response?.data?.error || "Ошибка загрузки";
                updateItem(i, { status: "error", error: msg });
            }
        }

        setUploading(false);
        updateContent();
    }

    const allDone = useMemo(
        () => items.length > 0 && items.every((x) => x.status === "done"),
        [items]
    );

    function clearFinished() {
        setItems(prev => prev.filter(x => x.status !== "done"));
    }

    // -------- delete / usage --------
    function handleDelete(el) {
        if (!window.confirm("Удалить этот файл?")) return;

        // у тебя сейчас удаление идёт через /putFile {id}
        // можно заменить на DELETE /admin/files/{id} — но не ломаем твой текущий флоу.
        api.post("/putFile", { id: el.id })
            .then(() => updateContent())
            .catch(() => alert("Ошибка удаления"));
    }

    function openUsage(el) {
        setUsageOpen(true);
        setUsageFile({ id: el.id, title: el.title || "Без названия" });
        setUsageLoading(true);
        setUsageError("");
        setUsageList([]);

        api.get(`/admin/files/${el.id}/usage`)
            .then((res) => setUsageList(res.data || []))
            .catch(() => setUsageError("Не удалось загрузить использование файла"))
            .finally(() => setUsageLoading(false));
    }

    function closeUsage() {
        setUsageOpen(false);
        setUsageFile(null);
        setUsageList([]);
        setUsageError("");
        setUsageLoading(false);
    }

    return (
        <div className="gallery-page">
            <div className="gallery-head">
                <h2>Галерея файлов</h2>
                <button className="add-btn" onClick={() => setShowLoadCard(true)}>
                    <span>＋</span> Новая загрузка
                </button>
            </div>

            {showLoadCard && (
                <form
                    className="gallery-card upload-card modern-upload"
                    onSubmit={uploadAll}
                >
                    <div className="modern-upload-header">
                        <span className="modern-upload-icon">📹</span>
                        <span className="modern-upload-title">Массовая загрузка</span>
                    </div>

                    <label
                        className={`modern-upload-drop ${dragOver ? "is-over" : ""}`}
                        onDragOver={onDragOver}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}
                    >
                        <input
                            type="file"
                            accept="video/*,image/*"
                            multiple
                            onChange={onFilePick}
                            style={{ display: "none" }}
                        />
                        <span>
                            Перетащите файлы сюда или нажмите, чтобы выбрать
                        </span>
                    </label>

                    {items.length > 0 && (
                        <div className="upload-list">
                            {items.map((it, idx) => (
                                <div
                                    key={`${it.file.name}-${it.file.size}-${idx}`}
                                    className={`upload-row ${it.status}`}
                                >
                                    <div className="upload-row-main">
                                        <div className="upload-name">{it.file.name}</div>

                                        <input
                                            className="modern-upload-input"
                                            type="text"
                                            value={it.title}
                                            onChange={(e) =>
                                                updateItem(idx, { title: e.target.value })
                                            }
                                            placeholder="Название"
                                        />

                                        <select
                                            className="modern-upload-select"
                                            value={it.category}
                                            onChange={(e) =>
                                                updateItem(idx, {
                                                    category: e.target.value,
                                                })
                                            }
                                        >
                                            {CATEGORY_OPTIONS.map((opt) => (
                                                <option
                                                    key={opt.value}
                                                    value={opt.value}
                                                >
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="upload-row-side">
                                        {it.status === "uploading" && (
                                            <div className="progress">
                                                <div
                                                    className="bar"
                                                    style={{ width: `${it.progress}%` }}
                                                />
                                                <span className="pct">{it.progress}%</span>
                                            </div>
                                        )}

                                        {it.status === "done" && (
                                            <span className="badge ok">Готово</span>
                                        )}

                                        {it.status === "error" && (
                                            <span className="badge err">{it.error}</span>
                                        )}

                                        <button
                                            type="button"
                                            className="modern-upload-cancel"
                                            onClick={() => removeItem(idx)}
                                            disabled={it.status === "uploading" && uploading}
                                            title="Убрать из очереди"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="gallery-actions modern-upload-actions">
                        <button
                            type="submit"
                            disabled={items.length === 0 || uploading}
                            className="modern-upload-btn"
                        >
                            {uploading ? "Загрузка..." : "Загрузить все"}
                        </button>

                        <button
                            type="button"
                            className="modern-upload-cancel"
                            onClick={() => setShowLoadCard(false)}
                        >
                            Закрыть
                        </button>

                        {allDone && (
                            <button
                                type="button"
                                className="secondary-btn"
                                onClick={clearFinished}
                            >
                                Очистить загруженные
                            </button>
                        )}
                    </div>
                </form>
            )}

            <div className="gallery-grid">
                {isDataLoad && mediaList.length === 0 && (
                    <div className="gallery-empty">Файлов нет</div>
                )}

                {mediaList.map((el) => {
                    const isVideo =
                        (typeof el.type === "string" &&
                            el.type.toLowerCase().startsWith("video")) ||
                        /\.(mp4|webm|mov|mkv|avi)$/i.test(el.url || "");

                    return (
                        <div className="gallery-card" key={el.id}>
                            <div className="gallery-media">
                                {isVideo ? (
                                    <video
                                        className="mediaGallery-mx-h"
                                        controls
                                        muted
                                        preload="metadata"
                                    >
                                        <source
                                            src={el.url}
                                            type={el.type || undefined}
                                        />
                                    </video>
                                ) : (
                                    <img
                                        className="mediaGallery-mx-h"
                                        src={el.url}
                                        alt={el.title || ""}
                                    />
                                )}
                            </div>

                            <div className="gallery-info">
                                <div className="gallery-title">
                                    {el.title || "Без названия"}
                                </div>

                                <div className="gallery-meta">
                                    <span>
                                        {isVideo ? "Видео" : "Картинка"}
                                    </span>
                                    <span className="gallery-url">
                                        {el.url}
                                    </span>
                                </div>

                                <div className="gallery-meta">
                                    <span className="gallery-cat">
                                        Тип показа:{" "}
                                        {categoryLabel(el.category)}
                                    </span>
                                </div>
                            </div>

                            <div className="gallery-actions">
                                <button
                                    className="secondary-btn"
                                    onClick={() => openUsage(el)}
                                >
                                    Где используется
                                </button>

                                <button
                                    className="delete-btn"
                                    onClick={() => handleDelete(el)}
                                >
                                    Удалить
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {usageOpen && (
                <div className="modal-overlay" onClick={closeUsage}>
                    <div
                        className="modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-header">
                            <div className="modal-title">Использование файла</div>
                            <button
                                className="modal-close"
                                onClick={closeUsage}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="modal-subtitle">
                            {usageFile?.title} (ID: {usageFile?.id})
                        </div>

                        {usageLoading && (
                            <div className="modal-loading">Загрузка…</div>
                        )}

                        {usageError && (
                            <div className="modal-error">{usageError}</div>
                        )}

                        {!usageLoading && !usageError && (
                            usageList.length === 0 ? (
                                <div className="modal-empty">
                                    Совпадений нет
                                </div>
                            ) : (
                                <div className="usage-list">
                                    {usageList.map((c) => (
                                        <div
                                            key={`${c.companyId}-${c.address || ""}`}
                                            className="usage-item"
                                        >
                                            <div className="usage-address">
                                                {c.address ||
                                                "Адрес не указан"}
                                            </div>

                                            <div className="usage-geo">
                                                {[
                                                    c.city,
                                                    c.region,
                                                    c.country,
                                                ]
                                                    .filter(Boolean)
                                                    .join(", ")}
                                            </div>

                                            <div className="usage-last">
                                                Последняя активность:{" "}
                                                {c.lastActive
                                                    ? new Date(
                                                        c.lastActive
                                                    ).toLocaleString()
                                                    : "—"}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )
                        )}

                        <div className="modal-footer">
                            <button
                                className="modern-upload-cancel"
                                onClick={closeUsage}
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
